// public/v3/src/lib/bhi-billing.js — pure BHI billing helpers.
//
// Lives separately from billing-generate.js so that Jest can require it
// directly (billing-generate.js uses ESM imports and can't be Jest-loaded).
// billing-generate.js imports from this module via ESM interop.
//
// Module format: CommonJS (module.exports). Pure functions only — no React,
// no fetches.
//
// Future tasks (1.10, 1.11) extend this module with assembleBhiBillingNote
// and buildBhiAddonAttestationLines — same Jest-readable home.

const BHI_BN_KEY = 'bhi_bn';

// CPT/HCPCS codes that count toward each program family for activity-log
// filtering. The patient's billing.program (CCM tier) is reflected as the
// entry.program value at log time. BHI, CPM, and PIN entries use their
// respective program codes directly.
//
// Background: production audit (2026-05-21) confirmed entry.program holds
// the CPT code ('99490', '99491', '99487', '99484', 'G0570'), not a family
// identifier like 'ccm' or 'bhi'. Phase 1's `e.program === 'bhi'` checks
// never matched anything in the wild. This is the canonical family-derivation
// helper — single source of truth, used by every activity-log filter.
const CCM_PROGRAM_CODES = new Set(['99490', '99491', '99487', '99439', '99437', '99489']);
const BHI_PROGRAM_CODES = new Set(['99484', 'G0570']);
// Chronic Pain Management — a third family so G3002 time stays disjoint from
// CCM/BHI minutes (a same-month stacking requirement, not a preference).
const PAIN_PROGRAM_CODES = new Set(['G3002', 'G3003']);
// Principal Illness Navigation — a fourth family so G0023/G0024 time stays
// disjoint from CCM/BHI/CPM minutes.
const NAV_PROGRAM_CODES = new Set(['G0023', 'G0024']);

function entryProgramFamily(entry) {
  const code = entry && entry.program;
  if (!code) return 'ccm'; // legacy untagged default
  if (BHI_PROGRAM_CODES.has(code)) return 'bhi';
  if (PAIN_PROGRAM_CODES.has(code)) return 'pain';
  if (NAV_PROGRAM_CODES.has(code)) return 'nav';
  return 'ccm'; // CCM_PROGRAM_CODES OR any unrecognized CCM-era CPT
}

function bhiMonthlyMinutes(patient, monthStr) {
  const raw = patient && patient._raw ? patient._raw : patient;
  const log = Array.isArray(raw && raw.activityLog) ? raw.activityLog : [];
  return log
    .filter((e) => e && !e.isConsolidatedNote && (e.date || '').startsWith(monthStr || '') && entryProgramFamily(e) === 'bhi')
    .reduce((acc, e) => acc + (parseInt(e.duration, 10) || 0), 0);
}

function resolveBhiBillCode(patient, monthStr) {
  const raw = patient && patient._raw ? patient._raw : patient;
  const apcmMap = (raw && raw.apcmMonth && typeof raw.apcmMonth === 'object') ? raw.apcmMonth : {};
  return (monthStr && apcmMap[monthStr]) ? 'G0570' : '99484';
}

// opts.assessmentOverride (bool): when true, the "no initial assessment on
// file" condition is DEMOTED from a hard blocker to a soft warning. It is
// surfaced on the returned `warnings` array instead of `blockers`, so the
// gate reads `ok` when that was the only outstanding reason. This backs the
// SOFT-guard override — an explicit, warned, user-initiated decision to bill
// BHI when no PHQ-9/PHQ-2 assessment is documented for the period.
// Enrollment, consent, and the 20-minute floor stay HARD regardless.
// Callers that omit `opts` behave exactly as before (no `warnings` key).
function bhiGatesForCode(patient, monthStr, code, opts) {
  const raw = patient && patient._raw ? patient._raw : patient;
  const assessmentOverride = !!(opts && opts.assessmentOverride);
  const blockers = [];
  const warnings = [];
  if (!raw || !raw.billing || !raw.billing.bhiProgram) blockers.push('BHI not enrolled');
  const consentSigned = raw && raw.bhiConsent && raw.bhiConsent.signed === true;
  if (!consentSigned) blockers.push('BHI consent missing');
  const assessments = raw && raw.bhi && raw.bhi.assessments;
  const latestAssessment = _latestAssessmentForMonth(assessments, monthStr);
  if (!latestAssessment) {
    if (assessmentOverride) warnings.push('Initial assessment missing');
    else blockers.push('Initial assessment missing');
  }
  if (code === '99484') {
    // Minute gate only applies to 99484. G0570 inherits APCM's flat-fee structure.
    const minutes = bhiMonthlyMinutes(raw, monthStr);
    if (minutes < 20) blockers.push('Only ' + minutes + ' BHI minutes logged this month (need 20)');
  }
  const result = { ok: blockers.length === 0, blockers };
  if (warnings.length) result.warnings = warnings;
  return result;
}

// BHI per-code minute targets. Mirrors BILLING_TIME_TARGETS from
// billing-generate.js but kept local since this module can't import from ESM.
// G0570 is flat-fee (no minute target) — APCM add-on attestation only.
const BHI_BILLING_TIME_TARGETS = { '99484': 20, 'G0570': 0 };

// ── Inline helpers (CommonJS port of billing-generate.js helpers) ────────────
// Inline helpers — ported from billing-generate.js with simplifications.
// Lives here (not imported from billing-generate.js) because billing-generate.js
// is ESM and not directly require-able from Jest. The simplifications are minor
// (more permissive null/undefined handling, no STAFF_CREDENTIALS allowlist check
// in _formatEntryStaff — credential comes from validated UI-populated entries).

function _conditionsToString(conditions) {
  if (typeof conditions === 'string') return conditions;
  return (conditions || []).map(function(c) { return typeof c === 'string' ? c : (c && (c.description || c.label || '')) ; }).filter(Boolean).join('; ');
}

function _formatCpMonth(ym) {
  const names = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const parts = (ym || '').split('-');
  const mi = parseInt(parts[1], 10) - 1;
  return (names[mi] || '') + ' ' + (parts[0] || '');
}

function _entryActivityType(entry) {
  const type = String((entry && (entry.type || entry.label || entry.desc)) || '').trim();
  return type || 'Other';
}

function _entryActivityNote(entry) {
  const e = entry || {};
  const note = String(e.notes || e.description || e.desc || e.hint || '').trim();
  if (!note) return '';
  const type = _entryActivityType(e);
  const label = String(e.label || '').trim();
  if (note === type || note === label) return '';
  return note;
}

function _serviceMonthEndDate(monthStr) {
  const m = String(monthStr || '').match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || mo < 1 || mo > 12) return null;
  const lastDay = new Date(y, mo, 0).getDate();
  return m[1] + '-' + m[2] + '-' + String(lastDay).padStart(2, '0');
}

function _assessmentDate(a) {
  const d = String((a && a.date) || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : '';
}

// Returns the assessment with the highest `date` (ISO YYYY-MM-DD sorts
// lexicographically by chronological order). Optional `throughDate` caps the
// selection to assessments documented on/before the service-period end date.
function _latestAssessmentByDate(assessments, throughDate) {
  if (!Array.isArray(assessments) || assessments.length === 0) return null;
  const cap = _assessmentDate({ date: throughDate });
  const eligible = assessments.filter(function(a) {
    const d = _assessmentDate(a);
    if (!d) return !cap;
    return !cap || d <= cap;
  });
  if (!eligible.length) return null;
  const sorted = eligible.slice().sort(function(a, b) {
    const da = _assessmentDate(a);
    const db = _assessmentDate(b);
    return db.localeCompare(da);
  });
  return sorted[0];
}

function _latestAssessmentForMonth(assessments, monthStr) {
  return _latestAssessmentByDate(assessments, _serviceMonthEndDate(monthStr));
}

function _formatEntryStaff(entry) {
  const staffName = String((entry && (entry.staff || entry.staffName || entry.createdByName || entry.authorName)) || '').trim();
  const cred = entry && entry.credential; // no STAFF_CREDENTIALS allowlist check — UI-populated only
  if (!staffName) return 'Clinical staff';
  return cred ? staffName + ', ' + cred : staffName;
}

// ── Pure assembly: standalone 99484 BHI billing visit note ───────────────────
// Pure function — takes inputs, returns the assembled note as a string.
// Lives here (CommonJS) so Jest can verify the exact content without bundling.
// Called by generateAndSaveBhiBillingNote in billing-generate.js after the AI
// step.
function assembleBhiBillingNote(patient, monthStr, aiText, cptCodes, totalMonthTime, activitySummary, carePlanOnFile) {
  const monthLabel = _formatCpMonth(monthStr);
  const patientName = [patient.fname, patient.lname].filter(Boolean).join(' ') || '(unknown)';
  const providerName = patient.provider || '';
  const latestAssessment = _latestAssessmentForMonth(patient && patient.bhi && patient.bhi.assessments, monthStr);

  const yy = parseInt(monthStr.split('-')[0]);
  const mm = parseInt(monthStr.split('-')[1]);
  const firstDay = monthStr + '-01';
  const lastDay = monthStr + '-' + new Date(yy, mm, 0).getDate();

  const codeOrder = [];
  const byCode = {};
  cptCodes.forEach(function(ci) {
    if (!byCode[ci.code]) {
      byCode[ci.code] = { code: ci.code, desc: ci.desc || '', units: 0, totalMin: 0 };
      codeOrder.push(ci.code);
    }
    byCode[ci.code].units += 1;
    byCode[ci.code].totalMin += (ci.target || 0);
  });
  const codeListInline = codeOrder.map(function(c) {
    const g = byCode[c];
    return c + (g.units > 1 ? ' ×' + g.units : '');
  }).join(' + ');

  const lines = [];
  lines.push('BHI BILLING VISIT NOTE — ' + monthLabel);
  lines.push('Patient: ' + patientName);
  if (providerName) lines.push('Billing Practitioner: ' + providerName);
  lines.push('Service Period: ' + firstDay + ' to ' + lastDay);
  lines.push('CPT Codes: ' + codeListInline);
  lines.push('Program: General Behavioral Health Integration (99484)');
  lines.push('');

  const bhiDx = (patient && patient.bhi && patient.bhi.dx) || _conditionsToString(patient.conditions);
  if (bhiDx) {
    lines.push('Behavioral health condition managed: ' + bhiDx);
    lines.push('');
  }

  if (latestAssessment) {
    lines.push('RATING SCALE ASSESSMENT:');
    lines.push('- ' + (latestAssessment.scale || '') + ' score ' + latestAssessment.score + (latestAssessment.severity ? ' (' + latestAssessment.severity + ')' : '') + ' on ' + (latestAssessment.date || ''));
    lines.push('');
  }

  lines.push((aiText || '').trim());
  lines.push('');

  lines.push('TIME DOCUMENTED THIS MONTH: ' + totalMonthTime + ' minutes');
  codeOrder.forEach(function(c) {
    const g = byCode[c];
    const minPart = (BHI_BILLING_TIME_TARGETS[c] || 0) > 0 ? ': ' + g.totalMin + ' min' : '';
    lines.push('- ' + c + (g.units > 1 ? ' (×' + g.units + ')' : '') + minPart + ' — ' + (g.desc || ''));
  });
  lines.push('');

  const entries = ((activitySummary && activitySummary.entries) || []).filter(function(e) { return entryProgramFamily(e) === 'bhi'; });
  if (entries.length) {
    lines.push('Itemized BHI time log:');
    const sorted = entries.slice().sort(function(a, b) { return (a.date || '').localeCompare(b.date || ''); });
    sorted.forEach(function(e) {
      const staffStr = _formatEntryStaff(e);
      const dur = parseInt(e.duration) || 0;
      const type = _entryActivityType(e);
      const activityNote = _entryActivityNote(e);
      const noteText = activityNote ? ' — ' + activityNote : '';
      lines.push('- ' + staffStr + ' — ' + dur + ' min — ' + type + noteText);
    });
    lines.push('');
  }

  lines.push('COMPLIANCE ATTESTATION:');
  const bhiConsent = patient.bhiConsent;
  if (bhiConsent && bhiConsent.signed && bhiConsent.date) {
    // Patient-link consent (the secure-link auto-enroll path) is electronic,
    // not verbal/written — the attestation must reflect how consent was
    // actually obtained (mirrors billing-generate.js's CCM consent line).
    const consentLabel = bhiConsent.source === 'patient-link'
      ? 'Electronic (patient-approved via secure link)'
      : (bhiConsent.source === 'written' ? 'Written' : 'Verbal');
    lines.push('- ' + consentLabel + ' consent for Behavioral Health Integration (BHI) was obtained on ' + bhiConsent.date + ' and remains active.');
  } else if (patient.consentDate) {
    lines.push('- Consent for Care Management (covering BHI under the practice\'s combined-consent policy) was obtained on ' + patient.consentDate + ' and remains active.');
  } else {
    // No recorded date — say exactly that instead of asserting standing consent.
    lines.push('- Verbal consent for Behavioral Health Integration (BHI) was attested at enrollment (see audit log); the original consent date is not separately recorded.');
  }
  // CMS-defensive: only attest an initial rating-scale assessment when one is
  // actually on file for the period. Under the assessment-override soft guard,
  // there is NO assessment — asserting "documented" would be a false
  // attestation to an audit reviewer. In that case, state the absence plainly
  // instead of claiming a scale is on file.
  if (latestAssessment) {
    lines.push('- Initial assessment with validated rating scale (' + latestAssessment.scale + ') documented; ongoing monitoring conducted with rating-scale follow-up as clinically appropriate.');
  } else {
    lines.push('- No validated rating-scale assessment (e.g., PHQ-9/PHQ-2) is on file for this period; behavioral-health monitoring was conducted without a documented initial rating scale.');
  }
  // CMS-defensive: only attest "care plan on file" when the caller has
  // confirmed an actual care_plan row exists. Asserting the plan exists when
  // none does would mislead an audit reviewer. The caller (in
  // billing-generate.js#generateAndSaveBhiBillingNote) looks up the
  // care_plan row for (patient, month) and passes the flag through.
  if (carePlanOnFile) {
    lines.push('- A behavioral health care plan is on file as part of the patient\'s comprehensive care plan.');
  }
  lines.push('- Care coordination with the treating clinician and any mental-health specialists is conducted as needed during this billing period.');
  lines.push('- Medication management, including attention to psychotropic safety, interactions, and adherence, is part of the behavioral health care plan and is addressed as clinically indicated.');
  lines.push('- Total BHI time documented reflects non-face-to-face behavioral-health care management and is exclusive of any separately billable evaluation and management services.');

  const isAlsoCcm = !!(patient.billing && patient.billing.program);
  if (isAlsoCcm) {
    lines.push('- This patient is also enrolled in CCM; the comprehensive care plan and CCM billing documentation are on file separately.');
  }

  return lines.join('\n');
}

// ── Pure G0570 add-on attestation builder ────────────────────────────────────
// Returns the deterministic G0570 add-on attestation block as a string array.
// Used by assembleCombinedBillingNote (in billing-generate.js) when an APCM
// month also has BHI gates satisfied. Pure — must contain NO minute language
// (G0570 is flat-fee; the combined note's minute totals belong to the APCM
// base code, not the BHI add-on).
function buildBhiAddonAttestationLines(patient, monthStr) {
  const lines = [];
  const latest = _latestAssessmentForMonth(patient && patient.bhi && patient.bhi.assessments, monthStr);
  const bhiDx = (patient && patient.bhi && patient.bhi.dx) || '';
  lines.push('BHI ADD-ON ATTESTATION (G0570):');
  if (bhiDx) lines.push('- Behavioral health condition managed: ' + bhiDx + '.');
  if (latest) {
    lines.push('- Initial assessment with validated rating scale: ' + latest.scale + ' score ' + latest.score + (latest.severity ? ' (' + latest.severity + ')' : '') + ' on ' + latest.date + '.');
  }
  // Signature-bound: this assembler has no care-plan lookup (unlike
  // assembleBhiBillingNote's carePlanOnFile), so the planning statement is an
  // explicit signing attestation, not a system assertion.
  lines.push('- By signing, the billing practitioner attests behavioral health care planning is addressed within the patient\'s comprehensive care plan.');
  if (latest) {
    lines.push('- Systematic monitoring with validated rating-scale follow-up performed during this billing period (' + latest.scale + ' on ' + latest.date + ').');
  } else {
    lines.push('- No validated rating-scale assessment is on file for this period; behavioral-health monitoring was conducted without a documented rating scale.');
  }
  return lines;
}

function resolveBhiBillCodeWithGates(patient, monthStr) {
  const code = resolveBhiBillCode(patient, monthStr);
  const gates = bhiGatesForCode(patient, monthStr, code);
  return { code, gatesOk: gates.ok, blockers: gates.blockers };
}

function ccmSlotLabel(patient, monthStr) {
  const raw = patient && patient._raw ? patient._raw : patient;
  const apcm = raw && raw.apcmMonth && raw.apcmMonth[monthStr];
  const ccmProgram = (raw && raw.billing && raw.billing.program) || '';
  const base = apcm && apcm.code ? `APCM ${apcm.code}` : `CCM ${ccmProgram}`;
  const bhiEnrolled = !!(raw && raw.billing && raw.billing.bhiProgram);
  if (apcm && bhiEnrolled) {
    const { gatesOk } = resolveBhiBillCodeWithGates(raw, monthStr);
    if (gatesOk) return `${base} + G0570`;
  }
  return base;
}

module.exports = {
  BHI_BN_KEY,
  CCM_PROGRAM_CODES,
  BHI_PROGRAM_CODES,
  NAV_PROGRAM_CODES,
  entryProgramFamily,
  bhiMonthlyMinutes,
  _latestAssessmentByDate,
  _latestAssessmentForMonth,
  resolveBhiBillCode,
  bhiGatesForCode,
  resolveBhiBillCodeWithGates,
  assembleBhiBillingNote,
  buildBhiAddonAttestationLines,
  ccmSlotLabel,
};
